import type { TimelineBoardKey } from '../types/schema'
import type { Workspace } from '../types/workspace'
import { BOARD_CODE_BY_KEY } from './publicIds'
import { isPersonalWorkspace } from './workspaceKind'

const CONSOLE_PREFIX = '/console'

const CONSOLE_ROOTS = new Set([
  'workspace',
  'personal',
  'settings',
  'your',
  'purchase',
  'invites',
  'members',
  'team',
  'explore',
  'upload',
  'link',
  'link-hub',
  'focus',
])

type ConsolePatchedHistory = History & { __timeliningConsolePatched?: boolean }

const splitUrl = (url: string) => {
  const hashIndex = url.indexOf('#')
  const beforeHash = hashIndex >= 0 ? url.slice(0, hashIndex) : url
  const hash = hashIndex >= 0 ? url.slice(hashIndex) : ''
  const queryIndex = beforeHash.indexOf('?')
  return {
    pathname: queryIndex >= 0 ? beforeHash.slice(0, queryIndex) : beforeHash,
    search: queryIndex >= 0 ? beforeHash.slice(queryIndex) : '',
    hash,
  }
}

export const isConsolePath = (pathname: string): boolean =>
  pathname === CONSOLE_PREFIX || pathname.startsWith(`${CONSOLE_PREFIX}/`)

export const stripConsolePrefix = (pathname: string): string => {
  if (pathname === CONSOLE_PREFIX) return '/'
  if (!pathname.startsWith(`${CONSOLE_PREFIX}/`)) return pathname
  return pathname.slice(CONSOLE_PREFIX.length) || '/'
}

export const getConsolePathname = (pathname = window.location.pathname): string =>
  stripConsolePrefix(pathname)

export const getConsoleParts = (pathname = window.location.pathname): string[] =>
  getConsolePathname(pathname).split('/').filter(Boolean)

export const isDesktopConsoleRoute = (pathname: string): boolean => {
  const parts = stripConsolePrefix(pathname).split('/').filter(Boolean)
  return parts.length > 0 && CONSOLE_ROOTS.has(parts[0])
}

export const legacyTeamRedirect = (parts: string[], search = ''): string | null => {
  if (parts[0] === 'invites' || parts[0] === 'members') {
    return toConsolePath(`/team${search}`)
  }
  return null
}

export const toConsolePath = (url: string): string => {
  if (!url || url.startsWith('#')) return url
  if (/^[a-zA-Z][a-zA-Z\d+\-.]*:/.test(url)) {
    try {
      const parsed = new URL(url)
      if (typeof window !== 'undefined' && parsed.origin === window.location.origin) {
        parsed.pathname = toConsolePath(parsed.pathname)
        return `${parsed.pathname}${parsed.search}${parsed.hash}`
      }
    } catch {
      return url
    }
    return url
  }
  if (!url.startsWith('/')) return url
  const { pathname, search, hash } = splitUrl(url)
  if (isConsolePath(pathname)) return url
  if (!isDesktopConsoleRoute(pathname)) return url
  return `${CONSOLE_PREFIX}${pathname === '/' ? '' : pathname}${search}${hash}`
}

export const installConsoleHistoryAdapter = (): void => {
  if (typeof window === 'undefined') return
  const history = window.history as ConsolePatchedHistory
  if (history.__timeliningConsolePatched) return
  history.__timeliningConsolePatched = true
  const originalPushState = history.pushState.bind(history)
  const originalReplaceState = history.replaceState.bind(history)
  history.pushState = (data: unknown, unused: string, url?: string | URL | null) => {
    const nextUrl = typeof url === 'string' ? toConsolePath(url) : url instanceof URL ? toConsolePath(url.toString()) : url
    originalPushState(data, unused, nextUrl)
  }
  history.replaceState = (data: unknown, unused: string, url?: string | URL | null) => {
    const nextUrl = typeof url === 'string' ? toConsolePath(url) : url instanceof URL ? toConsolePath(url.toString()) : url
    originalReplaceState(data, unused, nextUrl)
  }
}

export const normalizeLegacyConsoleUrl = (): void => {
  if (typeof window === 'undefined') return
  const nextPath = toConsolePath(`${window.location.pathname}${window.location.search}${window.location.hash}`)
  if (nextPath !== `${window.location.pathname}${window.location.search}${window.location.hash}`) {
    window.history.replaceState(window.history.state, '', nextPath)
  }
}

export const workspaceRootPath = (
  workspace: Pick<Workspace, 'isPersonal' | 'kind'> & { slug?: string },
  board: TimelineBoardKey = 'DASHBOARD',
): string => {
  if (isPersonalWorkspace(workspace)) return toConsolePath(`/personal/${BOARD_CODE_BY_KEY[board]}`)
  return toConsolePath(`/workspace/${workspace.slug}/${BOARD_CODE_BY_KEY[board]}`)
}
